import React, { useEffect, useState } from 'react';
import AdminModal from 'components/admin/AdminModal';
import { AirlinePicker } from 'components/admin';
import { toast } from 'react-toastify';

interface AddNonRevConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (airlineId: number, nonRevAirlineIds: number[]) => Promise<void>;
  initialData?: {
    airlineId: number;
    nonRevAirlineIds: number[];
    baseAirlineName?: string;
    nonRevAirlineNames?: string[];
  } | null;
}

const AddNonRevConfigModal: React.FC<AddNonRevConfigModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedBaseAirline, setSelectedBaseAirline] = useState<number[]>([]);
  const [selectedNonRevAirlines, setSelectedNonRevAirlines] = useState<number[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setSelectedBaseAirline([initialData.airlineId]);
        setSelectedNonRevAirlines(initialData.nonRevAirlineIds);
      } else {
        resetForm();
      }
    }
  }, [isOpen, initialData]);

  const resetForm = () => {
    setSelectedBaseAirline([]);
    setSelectedNonRevAirlines([]);
  };

  const handleSubmit = async () => {
    if (selectedBaseAirline.length === 0) {
      toast.error('Please select a base flight');
      return;
    }
    if (selectedNonRevAirlines.length === 0) {
      toast.error('Please select at least one non-rev flight');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(selectedBaseAirline[0], selectedNonRevAirlines);
      onClose();
    } catch (error) {
      // Error handling is done in parent
    } finally {
      setLoading(false);
    }
  };

  const handleBaseAirlineChange = (airlineIds: number[]) => {
    setSelectedBaseAirline(airlineIds);
    
    // If the selected base airline is in non-rev list, remove it
    if (airlineIds.length > 0 && selectedNonRevAirlines.includes(airlineIds[0])) {
      setSelectedNonRevAirlines(selectedNonRevAirlines.filter(id => id !== airlineIds[0]));
    }
  };

  const handleNonRevAirlinesChange = (airlineIds: number[]) => {
    // Filter out the base airline if it's selected
    const filteredIds = selectedBaseAirline.length > 0 
      ? airlineIds.filter(id => id !== selectedBaseAirline[0])
      : airlineIds;
    
    setSelectedNonRevAirlines(filteredIds);
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Configuration' : 'Add New Configuration'}
      size="2xl"
      className="min-h-[600px]"
      showCloseButton={true}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 text-sm bg-[#FFD700] text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Submitting...</span>
              </>
            ) : (
              'Submit'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-6 min-h-[400px]">
        {/* Base Flight Selection */}
        <div>
          <AirlinePicker
            selectedAirlineIds={selectedBaseAirline}
            onChange={handleBaseAirlineChange}
            placeholder="Search for base flight..."
            label="Base Flight Name"
            showChips={true}
            disabled={loading || !!initialData}
            isRequired={true}
            maxSelections={1}
            showConfirmButton={true}
            initialAirlineNames={initialData?.baseAirlineName ? [initialData.baseAirlineName] : []}
          />
        </div>

        {/* Non-Rev Flights Selection */}
        <div>
          <AirlinePicker
            selectedAirlineIds={selectedNonRevAirlines}
            onChange={handleNonRevAirlinesChange}
            placeholder="Search for non-rev flights..."
            label="Non-Rev Flights"
            showChips={true}
            disabled={loading}
            isRequired={true}
            showConfirmButton={true}
            initialAirlineNames={initialData?.nonRevAirlineNames || []}
          />
          {selectedBaseAirline.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Note: You cannot select {selectedBaseAirline.length > 0 ? 'the base flight' : ''} as a non-rev flight
            </p>
          )}
        </div>
      </div>
    </AdminModal>
  );
};

export default AddNonRevConfigModal;
